<template>
  <div id="app" :class="{ 'test-environment-style': isNotProduction }">
    <notification-container />
    <router-view :key="$route.fullPath" />
    <div v-if="pageLoadingStore.isLoading" class="page-loading-overlay">
      <inline-retailer-loading :is-message-showing="false" />
    </div>
    <!--
      Rendered here rather than where it is triggered: the veil is position: fixed, and a
      transformed ancestor anywhere up the tree would turn it into a containing block and crop it.

      The enter transition is skipped when the veil is already on screen — a panel switch raises it
      before the reload and this document only takes it over, so fading it in again would dip back
      to a page that is on its way out. The leave transition always runs: that one is the panel
      being revealed.
    -->
    <transition
      :enter-active-class="veilFadesIn ? 'veil-enter-active' : ''"
      :enter-from-class="veilFadesIn ? 'veil-enter-from' : ''"
      leave-active-class="veil-leave-active"
      leave-to-class="veil-leave-to"
    >
      <div v-if="pageLoadingStore.fullPage !== null" class="full-page-veil">
        <flex-loader v-if="pageLoadingStore.fullPage.brand === 'flex'" />
        <agriculture-loader v-else />
        <p class="font-18 mt-3">{{ pageLoadingStore.fullPage.message }}</p>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePageLoadingStore } from '@/stores/helpers/pageLoading.js'
import useBodyLoadingClass from '@/composables/useBodyLoadingClass.js'
import InlineRetailerLoading from '@/components/InlineRetailerLoading.vue'
import AgricultureLoader from '@/components/loaders/AgricultureLoader.vue'
import FlexLoader from '@/components/loaders/FlexLoader.vue'
import NotificationContainer from '@/components/NotificationContainer.vue'

// Nothing may keep the screen covered forever. Panel.vue lowers the veil once it has mounted, but a
// boot that ends anywhere else — the policy page, the login page, a failure — would otherwise leave
// the user staring at white over a page they could perfectly well use.
const HANDOVER_TIMEOUT_MS = 15_000

const isNotProduction = computed(() => import.meta.env.MODE !== 'production')
const pageLoadingStore = usePageLoadingStore()
const router = useRouter()

const veilFadesIn = computed(() => pageLoadingStore.fullPage?.fadeIn !== false)

useBodyLoadingClass(() => pageLoadingStore.fullPage !== null)

/**
 * Pick up a panel switch that the previous document started.
 *
 * The screen is already white: the previous document faded to it before navigating and index.html
 * repainted the same white in this document's first frame, so the swap had nothing to give away.
 * All that is left here is to put the loader back on that white and take the plain backdrop down —
 * next tick, once this veil has actually painted, or the page would show through in between.
 *
 * The handover arrives as a global rather than storage: it dies with the document, so a reload the
 * user asks for later cannot resurrect a switch that finished long ago.
 */
onMounted(async () => {
  const handover = window.panelSwitchHandover

  if (!handover) {
    return
  }

  pageLoadingStore.startFullPage(handover.message, handover.brand, { fadeIn: false })

  // Everything below may only ever take down the veil this boot raised. A second switch started
  // within the timeout raises a veil of its own, and neither the timer nor a later navigation may
  // pull that one out from under a reload that is still on its way.
  const raisedVeil = pageLoadingStore.fullPage

  const lowerVeil = () => {
    if (pageLoadingStore.fullPage === raisedVeil) {
      pageLoadingStore.stopFullPage()
    }
  }

  await nextTick()
  window.hideBootBackdrop?.()

  // Panel.vue lowers the veil on the normal path. This covers the boots that never reach it: a
  // getUser failure sends the user to the login page and an unaccepted policy to /retailer/policy,
  // and neither is under the panel layout — they would otherwise sit under white until the timer.
  const stopListening = router.afterEach(() => {
    stopListening()
    lowerVeil()
  })

  setTimeout(lowerVeil, HANDOVER_TIMEOUT_MS)
})
</script>

<style scoped>
.test-environment-style {
  margin-top: 20px;
}

.page-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

/*
 * Opaque, not the translucent white the other loaders use, and the same white index.html paints.
 * That is the whole trick behind the reload: if the screen is uniform at the moment the document
 * is replaced, the replacement has nothing to reveal and the seam cannot be seen.
 */
.full-page-veil {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #ffffff;
  text-align: center;
  z-index: 9999;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.veil-enter-active {
  transition: opacity 0.25s ease-out;
}

.veil-enter-from {
  opacity: 0;
}

.veil-leave-active {
  transition: opacity 0.4s ease-in;
}

.veil-leave-to {
  opacity: 0;
}
</style>
